import {FormControl, FormGroup, Validators} from '@angular/forms';

import {map, switchMap} from 'rxjs/operators';
import {of} from 'rxjs/internal/observable/of';
import {BehaviorSubject, Subject, Subscription} from 'rxjs';

import {
    ApiError,
    ApiResponse,
    ServerResponse,
    ServerResponseData,
    HttpStatusCodesEnum,
    LinkedAccountStatusesEnum,
    FileStorageService,
    LinkedAccountsService,
    LinkedAccountInterface,
    LinkedAccountResponseInterface,
    CountryModel,
    LinkedAccountModel,
    CompanyExtendedModel,
    IndividualExtendedModel,
    LinkedAccountResponseModel,
    CryptoCurrencyInterface,
} from '@spa/api';

import {BaseControl, Helper} from '../../../../shared/classes';

import {BankAccountsTrait} from '../../traits';
import {BankAccountTypesEnum, BankAccountsFormErrorStatesEnum} from '../../enums';

import {
    BankAccountBicSelector,
    BankAccountsFormAccountTypeSelector,
    BankAccountsFormBankCountrySelector,
    BankAccountsFormBicSelector,
    BankAccountsFormControls,
    BankAccountsFormControlsInterface,
    BankAccountsFormStatusSelector,
    CryptoNetworkCodeSelector,

} from '../../../../../assets/descriptors';
import {LatinDigitsValidator, MaxLengthValidatorKey, SwiftValidator, WhitespaceValidator} from '@spa/validators';

export class BankAccountsForm {
    private readonly linkedAccountsService: LinkedAccountsService;
    private readonly countries: CountryModel[];
    private readonly client: IndividualExtendedModel | CompanyExtendedModel;
    private readonly isCorporateClient: boolean;
    private readonly fileStorageService: FileStorageService;
    private readonly bicMaxLength = 11;
    private readonly cryptoCurrencies: CryptoCurrencyInterface[];

    private bankAccount: LinkedAccountModel;
    private cryptoNetworkCodeSubscription: Subscription;

    public form: FormGroup;
    public controls: BankAccountsFormControlsInterface;
    public controlsObservable: BehaviorSubject<BaseControl[]>;
    public documentFile: File;
    public documentErrorsSubject: Subject<string[]>;
    public accountTypeChangedSubscriptions: Subscription[];

    constructor(
        bankAccount: LinkedAccountModel,
        linkedAccountsService: LinkedAccountsService,
        countries: CountryModel[],
        client?: IndividualExtendedModel | CompanyExtendedModel,
        isCorporateClient?: boolean,
        fileStorageService?: FileStorageService,
        cryptoCurrencies?: CryptoCurrencyInterface[]
    ) {
        this.bankAccount = bankAccount;
        this.linkedAccountsService = linkedAccountsService;
        this.countries = countries;

        this.client = client;
        this.isCorporateClient = isCorporateClient;
        this.fileStorageService = fileStorageService;
        this.cryptoCurrencies = cryptoCurrencies || [];

        this.controlsObservable = new BehaviorSubject([]);
        this.documentFile = null;
        this.documentErrorsSubject = new Subject();

        this.accountTypeChangedSubscriptions = [];
        this.cryptoNetworkCodeSubscription = null;
    }

    private fixForm(accountType: number, floatingLabelNever: boolean): void {
        if (this.controls.beneficiaryBankCountry) {
            this.controls.beneficiaryBankCountry.value = !this.bankAccount ? null :
                this.bankAccount.accountInfo.bankCountryId;

            this.controls.beneficiaryBankCountry.disabled = false;
        }

        const controlNameMap: { [key: string]: string } = {};
        Object.keys(this.controls).forEach((key: string) => {
            if (this.controls[key]) {
                controlNameMap[key] = this.controls[key].name;
            }
        });

        BankAccountsTrait.fixControls(accountType, this.bankAccount, this.controls, floatingLabelNever, this.countries);

        const controls = [];

        Object.keys(this.controls).forEach((controlKey: string) => {
            if (this.controls[controlKey] !== null) {
                if (!this.form.get(this.controls[controlKey].name)) {
                    this.form.addControl(
                        this.controls[controlKey].name,
                        new FormControl(this.controls[controlKey].value, this.controls[controlKey].validators)
                    );
                }

                controls.push(this.controls[controlKey]);
            } else {
                const formKey = controlNameMap[controlKey] || controlKey;
                if (this.form.get(formKey)) {
                    this.form.removeControl(formKey);
                }
            }
        });

        if (this.controls.beneficiaryBankCountry) {
            this.form.get(BankAccountsFormBankCountrySelector)
                ?.setValue(this.controls.beneficiaryBankCountry.value, {emitEvent: false});
        }

        if (accountType === BankAccountTypesEnum.CryptoWallet) {
            this.setupCryptoOptions();
            this.documentErrorsSubject.next([]);
        } else {
            this.cryptoNetworkCodeSubscription?.unsubscribe();
            this.cryptoNetworkCodeSubscription = null;
        }

        this.changeBicValidationRegardingAccountType(accountType);

        this.form.updateValueAndValidity({emitEvent: false});

        this.controlsObservable.next(controls);
    }

    private setupCryptoOptions(): void {
        const networkCodes = this.cryptoCurrencies
            .map(c => c.network_code)
            .filter((value, index, self) => self.indexOf(value) === index);
        this.controls.crypto_network_code.options = networkCodes.map(code => ({label: code, value: code}));

        const updateCodeOptions = (networkCode: string) => {
            const codes = this.cryptoCurrencies
                .filter(c => c.network_code === networkCode)
                .map(c => c.code)
                .filter((value, index, self) => self.indexOf(value) === index);
            this.controls.crypto_code.options = codes.map(code => ({label: code, value: code}));
        };

        const currentNetworkCode = this.form.get(CryptoNetworkCodeSelector)?.value;
        if (currentNetworkCode) {
            updateCodeOptions(currentNetworkCode);
        }

        this.cryptoNetworkCodeSubscription?.unsubscribe();
        this.cryptoNetworkCodeSubscription = this.form.get(CryptoNetworkCodeSelector).valueChanges.subscribe(
            (networkCode: string) => updateCodeOptions(networkCode)
        );
    }

    private changeBicValidationRegardingAccountType(accountType: number): void {

        this.form.get(BankAccountsFormBicSelector)?.clearValidators();
        const standardValidators = [WhitespaceValidator(), LatinDigitsValidator()];

        if (accountType === BankAccountTypesEnum.BankAccount) {
            standardValidators.push(Validators.maxLength(this.bicMaxLength));
        } else {
            standardValidators.push(SwiftValidator());
        }

        this.form.get(BankAccountsFormBicSelector)?.setValidators(standardValidators);
        this.form.get(BankAccountsFormBicSelector)?.updateValueAndValidity();
    }

    private createRegistrationRequest(): ServerResponse<LinkedAccountModel> {

        const linkedAccountInputModel = BankAccountsTrait.createBakAccountInputModel(
            this.client,
            this.isCorporateClient,
            this.form,
            this.documentFile
        );

        return this.linkedAccountsService.register(linkedAccountInputModel).pipe(
            switchMap((registrationResponse: ServerResponseData<LinkedAccountResponseInterface>) => {
                if (registrationResponse.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponseObservable<LinkedAccountResponseInterface>(
                        registrationResponse,
                        BankAccountsFormErrorStatesEnum.UploadStatementFailed
                    );
                }

                const responseData = registrationResponse.data as LinkedAccountResponseInterface;
                const responseModel = new LinkedAccountResponseModel(responseData);

                return this.linkedAccountsService.getById(responseModel.accountId).pipe(
                    map((getResponse: ServerResponseData<LinkedAccountInterface>) => {
                        if (getResponse.status !== HttpStatusCodesEnum.Ok) {
                            return Helper.errorsHelper().createErrorResponse<LinkedAccountInterface>(
                                getResponse,
                                BankAccountsFormErrorStatesEnum.GetBankAccountFailed
                            );
                        }

                        const data = getResponse.data as LinkedAccountInterface;

                        this.bankAccount = new LinkedAccountModel(data);

                        return Helper.createSuccessResponse<LinkedAccountModel>(this.bankAccount);
                    })
                );
            })
        );
    }

    private createUpdateRequest(): ServerResponse<LinkedAccountModel> {
        const clonedModel = this.bankAccount.serialize();

        BankAccountsTrait.updateBankAccount(this.bankAccount, this.form);

        return this.linkedAccountsService.update(this.bankAccount).pipe(
            map((response: ServerResponseData<LinkedAccountResponseInterface>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    this.bankAccount = new LinkedAccountModel(clonedModel);

                    return Helper.errorsHelper().createErrorResponse<LinkedAccountResponseInterface>(
                        response,
                        BankAccountsFormErrorStatesEnum.UpdateBankAccountFailed
                    );
                }

                return Helper.createSuccessResponse<LinkedAccountModel>(this.bankAccount);
            })
        );
    }

    initForm(forEditing = false, floatingLabelNever = false, accountType = 0): void {
        this.form = new FormGroup({});

        this.controls = BankAccountsFormControls(
            floatingLabelNever,
            (this.bankAccount && (this.bankAccount.status !== LinkedAccountStatusesEnum.New))
        );

        if (!forEditing) {
            delete this.controls.status;
        }

        BankAccountsTrait.fixControlsOnInit(this.bankAccount, this.controls, this.countries);

        const controls = Object.keys(this.controls)
            .filter((controlKey: string) => (this.controls[controlKey] !== null))
            .map((controlKey: string) => {
                const control = this.controls[controlKey];

                control.value = BankAccountsTrait.initControlValue(this.bankAccount, control.name, control.value);

                const formControl = new FormControl(control.value, control.validators);

                if (controlKey === BankAccountsFormAccountTypeSelector) {
                    this.accountTypeChangedSubscriptions.push(formControl.valueChanges.subscribe((accountType: number) => {
                        this.fixForm(accountType, floatingLabelNever);
                    }));
                }

                this.form.addControl(control.name, formControl);

                return control;
            });

        if (this.controls.accountType.value === BankAccountTypesEnum.CryptoWallet) {
            this.setupCryptoOptions();
        }

        if (accountType > 0) {
            this.changeBicValidationRegardingAccountType(accountType);
        }

        this.controlsObservable.next(controls);
    }

    validateForm(messageFunction: (controlLabel?: string, controlMaxLength?: number) => { [key: string]: string; }): boolean {
        if (this.form.invalid) {
            Object.keys(this.controls)
                .filter((controlKey: string) => (this.controls[controlKey] !== null))
                .forEach((controlKey: string) => {
                    const control = this.controls[controlKey];
                    const formControl = this.form.get(control.name);

                    if (formControl && formControl.invalid) {
                        const errors = Object.keys(formControl.errors).map((errorKey: string) => {
                            return messageFunction(control.placeholder, (control.name === BankAccountBicSelector &&
                                errorKey === MaxLengthValidatorKey) ?
                                this.bicMaxLength : null)[errorKey];
                        });

                        control.setErrors(errors);
                    }
                });

            return false;
        }

        return true;
    }

    validateDocumentFile(messageFunction: (controlLabel?: string) => { [key: string]: string; }): boolean {
        if (this.form.value[BankAccountsFormAccountTypeSelector] === BankAccountTypesEnum.CryptoWallet) {
            this.documentErrorsSubject.next([]);
            return true;
        }

        const errorMessages = Helper.validateFile(this.documentFile, messageFunction);

        this.documentErrorsSubject.next(errorMessages);

        return (errorMessages.length === 0);
    }

    createRequest(
        authUserPermissions: string[] = [],
        deletePermissions: string[] = []
    ): ServerResponse<LinkedAccountModel> {
        if (this.form.dirty) {
            if (!this.bankAccount) {
                return this.createRegistrationRequest();
            }

            const currentStatus = this.bankAccount.status;
            const newStatus = this.form.value[BankAccountsFormStatusSelector];

            if (
                ([LinkedAccountStatusesEnum.New, LinkedAccountStatusesEnum.Active].indexOf(currentStatus) >= 0) &&
                (newStatus === LinkedAccountStatusesEnum.Inactive) &&
                !Helper.getUnionStrategyPermissionsCheck(deletePermissions, authUserPermissions)
            ) {
                const deletionError = new ApiError(BankAccountsFormErrorStatesEnum.DeleteBankAccountRestricted);
                const deletionResponse = new ApiResponse(HttpStatusCodesEnum.Unauthorized, [deletionError]);

                return of(deletionResponse);
            }

            return this.createUpdateRequest();
        }

        return Helper.createSuccessResponseObservable(null);
    }
}
